import { XIcon } from '@heroicons/react/solid';
import { Modal } from 'antd';
import { clsx } from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { normalizeProductName } from '@/utils/index';

import { ICourseRewards } from '../RewardSlider';
import ButtonDefault, { ButtonVariants } from '../../shared/basic/button';
import { notify } from '../../shared/basic/notify';
import { formateErrorObject } from '../../shared/formateErrorObject';
import CoinHeader from '../../../../assets/Icons/NewHeaderIcons/CoinHeader';
import { user } from '../../../atoms/user';
import { EventDetails, logEvent } from '../../../hooks/useClickEventTracking';
import { claimReward } from '../../../services/reward';
import { getUserProfile } from '../../../services/user';
import { User } from '../../../utils/interfaces/User';

export interface IBookReward {
  _id: string;
  title: string;
  image: string;
  digitalProductId: string;
  coin_price: number;
}

interface Props {
  handlePrev: () => void;
  handleNext: () => void;
  books: IBookReward[] | ICourseRewards[] | [];
  currentIndex: number;
  renderItem: (book: IBookReward, className: string) => React.ReactNode;
  type: 'book' | 'course';
}

const ImageSlider: React.FC<Props> = ({
  books,
  currentIndex,
  renderItem,
  type,
}) => {
  const [claimRewardLoading, setClaimRewardLoading] = useState(false);
  const [, setUserData] = useRecoilState(user);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const router = useRouter();
  const [updatedBooks, setUpdatedBooks] = useState<
    (IBookReward | ICourseRewards)[]
  >([]);
  const userData: User | undefined = useRecoilValue(user);

  const handleClaimReward = async (product_id: string, productName: string) => {
    setClaimRewardLoading(true);
    try {
      const result = await claimReward(product_id);
      const data = await getUserProfile();
      setUserData(data?.data);

      const eventParamsItems: EventDetails = {
        event_name: 'purchase',
        source: 'global',
        source_type: 'orders',
        source_id: result?.data?.id,
        sub_source: 'product',
        sub_source_id: product_id,
        unit: 'INR',
        currency: 'INR',
        value: books[currentIndex]?.coin_price || 0,
        sub_source_name: productName
          ? normalizeProductName(productName)
          : productName,
        meta: {
          payment_mode: 'coins',
        },
      };

      logEvent({
        eventDetails: eventParamsItems,
        userData: userData,
        pathname: router.pathname as string,
      });

      notify(result.message, 'success');
      router.push('/my-orders/');
    } catch (error: any) {
      notify(formateErrorObject(error), 'error');
    } finally {
      setClaimRewardLoading(false);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const positionClasses = {
    center: `z-20 scale-100 opacity-100 xl:translate-y-[1rem] ${type === 'book' ? '2xl:translate-y-[2rem]' : '2xl:translate-y-[2rem]'} `,
    left: `z-10 scale-[0.6] opacity-70  ${type === 'book' ? '-translate-x-[35vw] -translate-y-[2vh] 2xl:-translate-y-[12vh]' : '-translate-x-[43vw] -translate-y-[4vh]'}  md:-translate-y-[6vh]  xl:-translate-y-[10vh] lg:-translate-x-[25vw]`,
    right: `z-10 scale-[0.6] opacity-70 ${type === 'book' ? 'translate-x-[35vw] -translate-y-[2vh]' : 'translate-x-[43vw] -translate-y-[4vh]'}  md:-translate-y-[6vh] xl:-translate-y-[10vh] 2xl:-translate-y-[12vh]  lg:translate-x-[25vw]`,
    farLeft: `z-0 scale-[0.4] opacity-50 -translate-x-[85vw] block lg:-translate-x-[40vw]   ${type === 'book' ? '-translate-y-[50%]  2xl:-translate-y-[70%]' : '-translate-y-[80%]'}`,
    farRight: `z-0 scale-[0.4] opacity-50 block translate-x-[85vw] lg:translate-x-[40vw] ${type === 'book' ? '-translate-y-[50%] 2xl:-translate-y-[70%]' : '-translate-y-[80%]'}`,
    hidden: 'hidden opacity-0',
  };

  const getPositionClass = (index: number) => {
    const total = books.length;
    if (index === currentIndex) return positionClasses.center;
    if (index === (currentIndex - 1 + total) % total)
      return positionClasses.left;
    if (index === (currentIndex + 1) % total) return positionClasses.right;
    if (index === (currentIndex - 2 + total) % total)
      return positionClasses.farLeft;
    if (index === (currentIndex + 2) % total) return positionClasses.farRight;
    return positionClasses.hidden;
  };

  useEffect(() => {
    if (books.length) {
      setUpdatedBooks(books);
    }
  }, [books]);

  return (
    <>
      <div className='relative w-full p-8 flex flex-col items-center text-center text-white'>
        <div
          className={clsx(
            type === 'book' ? 'gap-y-12' : 'md:gap-y-12',
            'relative w-full flex flex-col items-center  justify-center',
          )}
        >
          <div
            className={clsx(
              type === 'book' ? '2xl:min-h-[350px]' : ' 2xl:min-h-[300px]',
              'relative w-full flex justify-center items-center min-h-[200px] lg:min-h-[250px] xl:min-h-[250px] ',
            )}
          >
            <AnimatePresence>
              {updatedBooks?.map((book, index) => (
                <motion.div
                  key={`${book._id}-${index}`}
                  className={clsx(
                    'absolute transition-all duration-700',
                    getPositionClass(index),
                  )}
                  initial={{ opacity: 0, scale: 0.75 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.75 }}
                  transition={{
                    opacity: { duration: 0.8, ease: 'easeInOut' },
                    scale: { type: 'spring', stiffness: 10, damping: 35 },
                  }}
                >
                  {renderItem(book, 'w-full h-full object-cover rounded-lg')}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          <div className='flex flex-col'>
            <p className='flex gap-x-2 justify-center text-xl pb-4 md:pb-8 md:text-2xl font-semibold'>
              {books[currentIndex]?.coin_price || 0} <CoinHeader />
            </p>
            <ButtonDefault
              size={1}
              variant={ButtonVariants.UNSTYLED}
              loading={claimRewardLoading}
              disabled={claimRewardLoading}
              className='w-min p-1 z-40 disabled:bg-cartBgColor bg-primary'
              onClick={() => {
                if (books[currentIndex]?.coin_price) {
                  books[currentIndex]?.coin_price >= (userData?.balance || 0)
                    ? setIsModalVisible(true)
                    : handleClaimReward(
                        books[currentIndex]?.digitalProductId || '',
                        books[currentIndex]?.title || '',
                      );
                }
              }}
            >
              <span className='flex  text-sm font-semibold px-3 md:px-6 gap-x-2 z-40 py-0.5 md:py-1 whitespace-nowrap'>
                Claim {type === 'book' ? 'Book' : 'Course'}
              </span>
            </ButtonDefault>
          </div>
        </div>
      </div>
      <Modal
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width='450px'
        className='bg-cartBgColor rounded-[16px]'
        closable={false}
        centered={true}
        destroyOnHidden
      >
        <div className='flex flex-col'>
          <div className='flex justify-end '>
            <span
              onClick={() => {
                handleCancel();
              }}
              className=' bg-[#FFFFFF1A] p-1 w-fit rounded-full cursor-pointer'
            >
              <XIcon className='w-4 h-4  text-[#9F9EA6]' />
            </span>
          </div>

          <div className=' text-center text-2xl font-semibold text-white mt-2 md:mt-0'>
            Not enough Z coins
          </div>
          <div className='text-sm md:text-base text-center mt-[8px]'>
            You don’t have enough coins to claim this product
          </div>
          <div className='mt-[28px]'>
            <ButtonDefault
              size={4}
              variant={ButtonVariants.WHITE}
              className='w-full text-base p-1'
              onClick={() => {
                router.push('/refer-earn');
                handleCancel();
              }}
            >
              Earn Now
            </ButtonDefault>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ImageSlider;
